import React, { useRef, useState } from "react";
import { CalendarClock, FileText, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, errMsg, viewAuthedFile } from "../lib/api";
import { compressImage, isImageFile } from "../lib/imageCompress";

const MAX_SIZE = 5 * 1024 * 1024; // 5 MB per file (any file type)

/**
 * Master-driven document upload section for collector Survey / Reassessment forms.
 * Labels come from Master Data → Document Types (municipal admin can rename/add).
 * Multiple files per label, any type, max 5MB each. Optional at submit — a
 * "collect later" date can be recorded for pending documents.
 *
 * docs state shape: [{doc_type_id, doc_label, files: [{file_id, filename, size}]}]
 */
export function DocumentUploads({ code, docTypes = [], docs = [], setDocs, dueDate, setDueDate, testIdPrefix = "docs" }) {
  const [busyType, setBusyType] = useState(null);
  const inputRefs = useRef({});

  if (!docTypes.length) return null;
  const entry = (tid) => docs.find((d) => d.doc_type_id === tid);

  const upload = async (dt, fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    // Phase 130: photographed documents become <= 200 KB JPEGs; PDFs / Word / Excel files go up untouched
    const files = [];
    for (const f of picked) files.push(isImageFile(f) ? ((await compressImage(f)).file || f) : f);
    for (const f of files) {
      if (f.size > MAX_SIZE) {
        toast.error(`'${f.name}' is ${(f.size / (1024 * 1024)).toFixed(1)} MB — max 5 MB per file`);
        return;
      }
    }
    setBusyType(dt.id);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f, f.name);
        fd.append("doc_type_id", dt.id);
        const r = await api.post(`/t/${code}/field/docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ file_id: r.data.file_id, filename: r.data.filename, size: r.data.size });
      }
      setDocs((prev) => {
        const ex = prev.find((d) => d.doc_type_id === dt.id);
        if (ex) return prev.map((d) => (d.doc_type_id === dt.id ? { ...d, files: [...d.files, ...uploaded] } : d));
        return [...prev, { doc_type_id: dt.id, doc_label: dt.doc_label, files: uploaded }];
      });
      toast.success(`${uploaded.length} file(s) attached to '${dt.doc_label}'`);
    } catch (err) { toast.error(errMsg(err)); }
    finally {
      setBusyType(null);
      if (inputRefs.current[dt.id]) inputRefs.current[dt.id].value = "";
    }
  };

  const removeFile = (tid, fid) =>
    setDocs((prev) => prev.map((d) => (d.doc_type_id === tid ? { ...d, files: d.files.filter((f) => f.file_id !== fid) } : d))
      .filter((d) => d.files.length > 0));

  return (
    <Card className="shadow-sm" data-testid={`${testIdPrefix}-card`}>
      <CardHeader className="pb-1">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Paperclip className="h-4 w-4" /> Documents (all file types, max 5 MB each)
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-1 space-y-2.5">
        {docTypes.map((dt) => {
          const e = entry(dt.id);
          return (
            <div key={dt.id} className="border rounded-lg px-2.5 py-2 space-y-1.5" data-testid={`${testIdPrefix}-type-${dt.doc_label}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium truncate">{dt.doc_label}</span>
                <label>
                  <input ref={(el) => { inputRefs.current[dt.id] = el; }} type="file" multiple className="hidden"
                    onChange={(ev) => upload(dt, ev.target.files)} data-testid={`${testIdPrefix}-input-${dt.doc_label}`} />
                  <Button type="button" variant="outline" size="sm" className="h-8" asChild disabled={busyType === dt.id}>
                    <span className="cursor-pointer" data-testid={`${testIdPrefix}-upload-${dt.doc_label}`}>
                      {busyType === dt.id ? "Uploading..." : "Upload"}
                    </span>
                  </Button>
                </label>
              </div>
              {(e?.files || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {e.files.map((f) => (
                    <span key={f.file_id} className="inline-flex items-center gap-1 text-[11px] bg-muted rounded px-1.5 py-0.5 max-w-[220px]">
                      <FileText className="h-3 w-3 shrink-0" />
                      <button type="button" className="truncate underline-offset-2 hover:underline text-left"
                        title={`View ${f.filename}`}
                        onClick={() => viewAuthedFile(`/t/${code}/field/docs/${f.file_id}`, f.filename).catch(() => toast.error("Could not open document"))}
                        data-testid={`${testIdPrefix}-view-${f.file_id}`}>
                        {f.filename}
                      </button>
                      <button type="button" className="opacity-60 hover:opacity-100" onClick={() => removeFile(dt.id, f.file_id)}
                        aria-label={`Remove ${f.filename}`} data-testid={`${testIdPrefix}-remove-${f.file_id}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {setDueDate && (
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1.5">
              <CalendarClock className="h-3.5 w-3.5" /> Collect pending documents on (optional)
            </Label>
            <Input type="date" className="h-10" value={dueDate || ""} onChange={(e) => setDueDate(e.target.value)}
              data-testid={`${testIdPrefix}-due-date`} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
